import { sortBy } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import {
  type PhoneNumberData,
  type PhoneNumberLine,
  type PhoneNumberType,
} from './FormikMultiPhoneNumbers.types';

import { DEFAULT_PHONE_NUMBER_DATA, TYPES, typesSet } from './FormikMultiPhoneNumbers.data';

type FieldPredicates = {
  [Key in keyof Omit<PhoneNumberData, 'id'>]: (data: unknown) => data is PhoneNumberData[Key];
};

type InitialPhoneNumberInput =
  | {
      phoneNumbers?: InitialPhoneNumberInputItem[];
    }
  | InitialPhoneNumberInputItem[]
  | undefined;

type InitialPhoneNumberInputItem = {
  countryCode: string;
  fullPhoneNumber: string;
  id?: string | null;
  isDefault: boolean;
  number: string;
  remark?: string | null;
  type: PhoneNumberType | 'Unspecified';
};

const createPhoneNumberLine = (data?: PhoneNumberData, asDefault = false): PhoneNumberLine => {
  const inputId = uuidv4();

  const additionalData = {
    inputId,
    isDefault: data?.isDefault ?? asDefault,
  };

  return data
    ? { ...data, ...additionalData }
    : { ...DEFAULT_PHONE_NUMBER_DATA, ...additionalData };
};

const createFieldGuard =
  <TField extends keyof PhoneNumberData>(
    field: TField,
    predicate: (data: { [Key in TField]: unknown }) => boolean,
  ) =>
  <TData extends object>(data: TData): data is TData & Pick<PhoneNumberData, TField> =>
    Object.hasOwn(data, field) && predicate(data as { [Key in TField]: unknown });

const fieldPredicates: FieldPredicates = {
  countryCode: (data) => typeof data === 'string',
  isDefault: (data) => typeof data === 'boolean',
  number: (data) => typeof data === 'string',
  remark: (data) => data === null || typeof data === 'string',
  type: (data): data is PhoneNumberType => typeof data === 'string' && typesSet.has(data),
};

const fieldGuards = {
  countryCode: createFieldGuard('countryCode', (data) =>
    fieldPredicates.countryCode(data.countryCode),
  ),
  isDefault: createFieldGuard('isDefault', (data) => fieldPredicates.isDefault(data.isDefault)),
  number: createFieldGuard('number', (data) => fieldPredicates.number(data.number)),
  remark: createFieldGuard('remark', (data) => fieldPredicates.remark(data.remark)),
  type: createFieldGuard('type', (data) => fieldPredicates.type(data.type)),
};

const normalizePhoneNumberInput = (input: InitialPhoneNumberInput) => {
  const list = Array.isArray(input) ? input : input?.phoneNumbers;

  if (!list) return undefined;

  return sortBy(list, (item) => !item.isDefault);
};

const getInitialPhoneNumbers = (input: InitialPhoneNumberInput): PhoneNumberLine[] | undefined =>
  normalizePhoneNumberInput(input)?.map((phoneNumber) => ({
    countryCode: phoneNumber.countryCode,
    id: phoneNumber.id || null,
    inputId: uuidv4(),
    isDefault: phoneNumber.isDefault,
    number: phoneNumber.number,
    remark: phoneNumber.remark || null,
    type: phoneNumber.type === 'Unspecified' ? TYPES.MOBILE : phoneNumber.type,
  }));

const hasLineId = (data: unknown): data is { inputId: string } =>
  !!data &&
  typeof data === 'object' &&
  'inputId' in data &&
  !!data.inputId &&
  typeof data.inputId === 'string';

const isPhoneNumberLine = (data: unknown): data is PhoneNumberLine => {
  if (!data || typeof data !== 'object') return false;

  const { countryCode, isDefault, number, remark, type } = fieldGuards;

  return (
    countryCode(data) &&
    isDefault(data) &&
    number(data) &&
    remark(data) &&
    type(data) &&
    hasLineId(data)
  );
};

export {
  createPhoneNumberLine,
  fieldGuards,
  fieldPredicates,
  getInitialPhoneNumbers,
  isPhoneNumberLine,
};
